import { Box, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface Props {
  /** Diameter in px, passed through to MUI. */
  size?: number;
  /** What the user is waiting for. Defaults to a generic "Loading…". */
  label?: string;
  /** Passed through to CircularProgress (e.g. "inherit" inside a button). */
  color?: 'primary' | 'secondary' | 'inherit';
}

/**
 * REV-561: an accessible indeterminate spinner.
 *
 * A bare `<CircularProgress />` failed two of our own engine rules at once:
 *
 *   - ARIA-12 — `role="progressbar"` with neither `aria-valuenow` nor
 *     `aria-valuetext`. Omitting `aria-valuenow` is correct for an
 *     indeterminate bar per ARIA, but the engine (and its own passing fixture)
 *     expects `aria-valuetext` to state the indeterminate case explicitly.
 *   - NON-TEXT-CONTENT-01 — MUI's inner `<svg>` carries no text alternative.
 *
 * Rather than argue with our own engine on a page we ship, this owns the
 * semantics: the wrapper is the progressbar, and MUI's spinner (svg and all) is
 * marked `aria-hidden` as the decoration it is. That yields exactly one
 * progressbar with a real name — hiding the inner one matters, because
 * CircularProgress sets `role="progressbar"` itself and two nested progressbars
 * announce as two separate indicators.
 *
 * Dogfood note: we sell an accessibility product. Our own spinner should not be
 * the thing our engine flags.
 */
export default function LoadingSpinner({ size = 24, label, color }: Props) {
  const { t } = useTranslation();
  const text = label ?? t('common.loading', 'Loading...');

  return (
    <Box
      role="progressbar"
      aria-label={text}
      aria-valuetext={text}
      sx={{ display: 'inline-flex', lineHeight: 0 }}
    >
      <CircularProgress size={size} color={color} aria-hidden="true" />
    </Box>
  );
}
